import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { AvailabilityFilters } from "./types";

const csvInts = (v: string | null): number[] =>
  v
    ? v
        .split(",")
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => Number.isFinite(n))
    : [];

const csvStrings = (v: string | null): string[] =>
  v
    ? v
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

const parseNum = (v: string | null): number | null => {
  if (v === null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const parseBool = (v: string | null): boolean | null => {
  if (v === "true") return true;
  if (v === "false") return false;
  return null;
};

const defaultMoveInDate = (): string => {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return d.toISOString().slice(0, 10);
};

export function useAvailabilityFilters(): {
  filters: AvailabilityFilters;
  setFilter: <K extends keyof AvailabilityFilters>(
    key: K,
    value: AvailabilityFilters[K]
  ) => void;
  setFilters: (updates: Partial<AvailabilityFilters>) => void;
  clearFacets: () => void;
  reuseApplicationId: string | null;
  reuseReturnTo: string | null;
} {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo<AvailabilityFilters>(() => {
    return {
      moveInDate: searchParams.get("moveInDate") || defaultMoveInDate(),
      bedrooms: csvInts(searchParams.get("bedrooms")),
      bathrooms: csvInts(searchParams.get("bathrooms")),
      floorLevel: csvInts(searchParams.get("floorLevel")),
      petsPermitted: parseBool(searchParams.get("petsPermitted")),
      rentMin: parseNum(searchParams.get("rentMin")),
      rentMax: parseNum(searchParams.get("rentMax")),
      sqftMin: parseNum(searchParams.get("sqftMin")),
      sqftMax: parseNum(searchParams.get("sqftMax")),
      unitType: searchParams.get("unitType"),
      unitNumber: searchParams.get("unitNumber"),
      // Accept multi (?floorplans=a,b) and legacy single (?floorplan=a)
      floorplans: csvStrings(
        searchParams.get("floorplans") || searchParams.get("floorplan")
      ),
      page: parseNum(searchParams.get("page")) ?? 1,
      perPage: parseNum(searchParams.get("perPage")) ?? 50,
    };
  }, [searchParams]);

  const writeParams = useCallback(
    (next: AvailabilityFilters) => {
      const out = new URLSearchParams();
      const applicationId = searchParams.get("applicationId");
      const returnTo = searchParams.get("returnTo");
      if (applicationId) out.set("applicationId", applicationId);
      if (returnTo) out.set("returnTo", returnTo);
      out.set("moveInDate", next.moveInDate);
      if (next.bedrooms.length) out.set("bedrooms", next.bedrooms.join(","));
      if (next.bathrooms.length) out.set("bathrooms", next.bathrooms.join(","));
      if (next.floorLevel.length)
        out.set("floorLevel", next.floorLevel.join(","));
      if (next.petsPermitted !== null)
        out.set("petsPermitted", String(next.petsPermitted));
      if (next.rentMin !== null) out.set("rentMin", String(next.rentMin));
      if (next.rentMax !== null) out.set("rentMax", String(next.rentMax));
      if (next.sqftMin !== null) out.set("sqftMin", String(next.sqftMin));
      if (next.sqftMax !== null) out.set("sqftMax", String(next.sqftMax));
      if (next.unitType) out.set("unitType", next.unitType);
      if (next.unitNumber) out.set("unitNumber", next.unitNumber);
      if (next.floorplans.length)
        out.set("floorplans", next.floorplans.join(","));
      if (next.page && next.page !== 1) out.set("page", String(next.page));
      if (next.perPage && next.perPage !== 50)
        out.set("perPage", String(next.perPage));
      setSearchParams(out, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const setFilter = useCallback(
    <K extends keyof AvailabilityFilters>(
      key: K,
      value: AvailabilityFilters[K]
    ) => {
      const next = { ...filters, [key]: value };
      // changing any facet resets to page 1
      if (key !== "page" && key !== "perPage") next.page = 1;
      writeParams(next);
    },
    [filters, writeParams]
  );

  const setFilters = useCallback(
    (updates: Partial<AvailabilityFilters>) => {
      writeParams({
        ...filters,
        ...updates,
        page: "page" in updates ? (updates.page as number) : 1,
      });
    },
    [filters, writeParams]
  );

  const clearFacets = useCallback(() => {
    writeParams({
      ...filters,
      bedrooms: [],
      bathrooms: [],
      floorLevel: [],
      petsPermitted: null,
      rentMin: null,
      rentMax: null,
      sqftMin: null,
      sqftMax: null,
      unitType: null,
      unitNumber: null,
      floorplans: [],
      page: 1,
    });
  }, [filters, writeParams]);

  return {
    filters,
    setFilter,
    setFilters,
    clearFacets,
    reuseApplicationId: searchParams.get("applicationId"),
    reuseReturnTo: searchParams.get("returnTo"),
  };
}
